import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Empty, PageHeader, Table } from '../components/ui';
import { MASTER_STALE_MS, masterStatus, onMasterChange, syncMaster, type MasterStatus } from '../count/master';
import { hm, qty } from '../lib/format';
import { useOnline } from '../lib/online';
import { errMsg } from '../lib/useApi';
import { useOutbox } from '../outbox';

/** What this device still has to send, what was rejected, and how fresh the offline master is. */
export default function SyncPage() {
  const box = useOutbox();
  const online = useOnline();
  const [master, setMaster] = useState<MasterStatus | null>(null);
  const [busy, setBusy] = useState<'sync' | 'master' | null>(null);
  const [error, setError] = useState('');
  const [persisted, setPersisted] = useState<boolean | null>(null);

  useEffect(() => {
    const refresh = () => void masterStatus().then(setMaster);
    refresh();
    void navigator.storage?.persisted?.().then(setPersisted);
    return onMasterChange(refresh);
  }, []);

  const mine = box.ops().filter((o) => o.user_id === box.currentUser);
  const pending = mine.filter((o) => o.status === 'PENDING');
  const rejected = mine.filter((o) => o.status === 'REJECTED');
  const stale = master?.synced_at ? Date.now() - Date.parse(master.synced_at) > MASTER_STALE_MS : true;

  const run = async (kind: 'sync' | 'master') => {
    setBusy(kind);
    setError('');
    try {
      if (kind === 'sync') await box.flush();
      else setMaster(await syncMaster());
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const label = (o: (typeof mine)[number]) =>
    o.kind === 'ENTRY' ? `${o.entry.item_code} ${o.item_name} · ${qty(o.entry.original_qty)} ${o.entry.uom_code}` : `ยกเลิกรายการ (${o.reason})`;

  return (
    <>
      <PageHeader
        title="สถานะ Sync"
        subtitle={
          <>
            รายการในเครื่องนี้ที่ยังไม่ได้ส่งขึ้นระบบ และข้อมูลสินค้าสำหรับนับแบบออฟไลน์ · เวอร์ชัน <span className="font-mono">{__BUILD_ID__}</span>
          </>
        }
      />
      {error && (
        <div className="mb-3">
          <Alert>{error}</Alert>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Card title="การส่งข้อมูล">
          <div className="space-y-1 text-sm">
            <div>
              เครือข่าย: {online && box.online ? <Badge tone="ok">Online</Badge> : <Badge tone="bad">Offline</Badge>}
            </div>
            <div>
              รอ Sync: <b className={pending.length ? 'text-warn' : ''}>{pending.length}</b> รายการ · ถูกปฏิเสธ: <b className={rejected.length ? 'text-bad' : ''}>{rejected.length}</b>
            </div>
            <div className="text-muted">ส่งสำเร็จล่าสุด: {box.lastSyncAt ? new Date(box.lastSyncAt).toLocaleString('th-TH') : '-'}</div>
            {box.lastError && <div className="text-bad">ล่าสุดส่งไม่สำเร็จ: {box.lastError}</div>}
            <div className="text-muted">
              ป้องกัน Browser ลบข้อมูล: {persisted == null ? '-' : persisted ? 'เปิดแล้ว' : 'Browser ไม่อนุญาต (อย่าล้างข้อมูล Browser)'}
            </div>
          </div>
          <Button className="mt-3" variant="primary" loading={busy === 'sync'} disabled={!online || !pending.length} onClick={() => void run('sync')}>
            Sync ตอนนี้
          </Button>
        </Card>
        <Card title="ข้อมูลสินค้าในเครื่อง (ออฟไลน์)">
          <div className="space-y-1 text-sm">
            <div>
              สินค้า {master?.item_count.toLocaleString() ?? '-'} · Barcode {master?.barcode_count.toLocaleString() ?? '-'}
            </div>
            <div className={stale ? 'text-warn' : 'text-muted'}>
              อัปเดตล่าสุด: {master?.synced_at ? new Date(master.synced_at).toLocaleString('th-TH') : 'ยังไม่เคย'}
              {stale && ' — ควรอัปเดต'}
            </div>
          </div>
          <Button className="mt-3" loading={busy === 'master'} disabled={!online} onClick={() => void run('master')}>
            อัปเดตข้อมูลสินค้า
          </Button>
        </Card>
      </div>

      <Card title="รายการที่ยังไม่ส่ง / ถูกปฏิเสธ" className="mt-4">
        {pending.length + rejected.length === 0 ? (
          <Empty>ไม่มีรายการค้าง — ทุกอย่างส่งขึ้นระบบแล้ว</Empty>
        ) : (
          <Table head={['เวลา', 'รายการ', 'สถานะ']} minWidth={480}>
            {[...rejected, ...pending].map((o) => (
              <tr key={o.key}>
                <td className="whitespace-nowrap">{hm(o.created_at)}</td>
                <td>
                  {label(o)}
                  {o.error && <div className="text-xs text-bad">{o.error}</div>}
                </td>
                <td>{o.status === 'PENDING' ? <Badge tone="warn">รอส่ง ({o.attempts} ครั้ง)</Badge> : <Badge tone="bad">ถูกปฏิเสธ {o.error_code}</Badge>}</td>
              </tr>
            ))}
          </Table>
        )}
        {rejected.length > 0 && (
          <p className="mt-2 text-xs text-muted">รายการที่ถูกปฏิเสธถูกเก็บไว้ที่ระบบ (Sync Errors) ให้ Supervisor ตรวจ — ไม่ต้องส่งซ้ำ</p>
        )}
      </Card>
    </>
  );
}
